import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaTicketAlt, FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import api from '../../../services/api';

const PAGE_SIZE = 100;

const formatWeight = (w) =>
  (w || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Deepest drill level: the individual tickets behind the current scope.
// Paginated; refetches whenever the scope or page changes.
const TicketsModal = ({ scope, onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const fetchTickets = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.locationAnalytics.getDrillTickets(
        { ...scope, page, limit: PAGE_SIZE },
        { signal: abortRef.current.signal }
      );
      setTickets(data.tickets);
      setTotal(data.total);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets');
      }
    } finally {
      setLoading(false);
    }
  }, [scope, page]);

  useEffect(() => {
    fetchTickets();
    return () => abortRef.current && abortRef.current.abort();
  }, [fetchTickets]);

  // Reset to page 1 if the scope itself changes.
  useEffect(() => {
    setPage(1);
  }, [scope.location, scope.product, scope.partner, scope.type, scope.startDate, scope.endDate]);

  // Accessibility: focus the dialog on open, trap Tab inside it, close on Esc,
  // and restore focus to the trigger on close.
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return undefined;
    const previouslyFocused = document.activeElement;
    const focusable = () =>
      Array.from(
        node.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
    (focusable()[0] || node).focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const f = focusable();
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const scopeChips = [
    scope.location && { label: 'Location', value: scope.location },
    scope.product && { label: 'Product', value: scope.product },
    scope.partner && { label: 'Linked to', value: scope.partner },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${scope.type} tickets`}
        tabIndex={-1}
        className="bg-surface rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
              <FaTicketAlt className="mr-2 text-primary-500" />
              {scope.type} Tickets
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({total.toLocaleString()})
              </span>
            </h2>
            {scopeChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {scopeChips.map((c) => (
                  <span
                    key={c.label}
                    className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full"
                  >
                    <span className="text-gray-400">{c.label}:</span> {c.value}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : loading ? (
            <div className="p-12 text-center text-gray-400">
              <FaSpinner className="animate-spin mx-auto text-2xl mb-2" />
              Loading tickets…
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              No tickets found for this selection.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2">Ticket</th>
                  <th className="px-3 py-2">Date / Time</th>
                  <th className="px-3 py-2">Dispatch</th>
                  <th className="px-3 py-2">Receipt</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Vehicle</th>
                  <th className="px-3 py-2">Haulier</th>
                  <th className="px-3 py-2 text-right">Nett (t)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tickets.map((t, i) => (
                  <tr key={`${t.ticketNo}-${i}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap tabular-nums">
                      {t.ticketNo}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">
                      {t.timestamp}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{t.dispatchLocation}</td>
                    <td className="px-3 py-2 text-gray-600">{t.receiptLocation}</td>
                    <td className="px-3 py-2 text-gray-600">{t.product}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{t.vehicleReg}</td>
                    <td className="px-3 py-2 text-gray-600">{t.haulier}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-800 whitespace-nowrap tabular-nums">
                      {formatWeight(t.nettWeight)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer / pagination */}
        {total > PAGE_SIZE && (
          <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="text-xs" /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <FaChevronRight className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketsModal;
